import React, { useState, useCallback } from "react";
import useApi from "../../../hooks/useApi";

// ── Source definitions ────────────────────────────────────────────────────────

interface CrmSource {
  id: string;
  label: string;
  logo: string;           // emoji or initials badge
  color: string;          // border accent when selected
  description: string;
  syncMode: string;       // how data flows
  available: boolean;     // false = "connect first" state
  comingSoon?: boolean;
}

const SOURCES: CrmSource[] = [
  {
    id: "velaro",
    label: "Velaro CRM",
    logo: "V",
    color: "#4F6FF5",
    description: "Your native Velaro contacts — customers who have chatted, submitted forms, or been added manually.",
    syncMode: "One-time snapshot from your live contact database.",
    available: true,
  },
  {
    id: "hubspot",
    label: "HubSpot",
    logo: "🟠",
    color: "#FF7A59",
    description: "Import contacts from HubSpot CRM — filter by lifecycle stage, list membership, or owner.",
    syncMode: "One-time import now. Scheduled sync coming in Phase 2.",
    available: false,
  },
  {
    id: "dynamics",
    label: "Dynamics 365",
    logo: "🔵",
    color: "#0078D4",
    description: "Pull contacts or leads from Microsoft Dynamics 365 Sales or Customer Service.",
    syncMode: "One-time import. Live sync via Power Automate in Phase 2.",
    available: false,
  },
  {
    id: "pipedrive",
    label: "Pipedrive",
    logo: "🟢",
    color: "#21B350",
    description: "Import people from Pipedrive — filter by pipeline, deal stage, or custom field.",
    syncMode: "One-time import now. Scheduled sync coming in Phase 2.",
    available: false,
  },
  {
    id: "salesforce",
    label: "Salesforce",
    logo: "☁️",
    color: "#00A1E0",
    description: "Import leads or contacts from Salesforce — filter by campaign, record type, or owner.",
    syncMode: "One-time import now. Scheduled sync in Phase 2.",
    available: false,
  },
  {
    id: "csv",
    label: "CSV Upload",
    logo: "📄",
    color: "#64748B",
    description: "Upload a spreadsheet with columns: Email, Name, Phone, Tags. Max 10,000 rows.",
    syncMode: "One-time import from file.",
    available: true,
    comingSoon: false,
  },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface VelaroFilters {
  requireEmail: boolean;
  requirePhone: boolean;
  company: string;
  country: string;
  createdAfter: string;
}

interface PreviewResult {
  count: number;
  preview: { name: string; email: string; phone?: string; company?: string }[];
  stub?: boolean;
  message?: string;
}

interface Props {
  /**
   * "campaign-list" (default) — imports into a ContactList for use in campaigns.
   *   onImported receives (listId, listName).
   * "contacts" — imports directly into the native Contacts table.
   *   onImported receives (0, "") — just signals completion.
   */
  mode?: "campaign-list" | "contacts";
  /** Called when import completes */
  onImported: (listId: number, listName: string) => void;
  onClose: () => void;
  /** Pre-select a destination list (campaign-list mode only) */
  existingListId?: number;
  existingListName?: string;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function CrmAudienceImporter({ mode = "campaign-list", onImported, onClose, existingListId, existingListName }: Props) {
  const api = useApi();
  const [step, setStep]       = useState<1 | 2 | 3>(1);
  const [source, setSource]   = useState<CrmSource | null>(null);

  // Step 2: Velaro-specific filters
  const [filters, setFilters] = useState<VelaroFilters>({
    requireEmail: true,
    requirePhone: false,
    company:      "",
    country:      "",
    createdAfter: "",
  });

  // Step 3: destination + preview
  const [listMode, setListMode]       = useState<"new" | "existing">(existingListId ? "existing" : "new");
  const [newListName, setNewListName] = useState("");
  const [preview, setPreview]         = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing]   = useState(false);
  const [importing, setImporting]     = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // ── Step 2 → 3: preview ──

  const runPreview = useCallback(async () => {
    if (!source) return;
    setPreviewing(true);
    setError(null);
    try {
      const filterPayload = {
        source:      source.id,
        requireEmail:  filters.requireEmail,
        requirePhone:  filters.requirePhone,
        company:       filters.company   || undefined,
        country:       filters.country   || undefined,
        createdAfter:  filters.createdAfter ? new Date(filters.createdAfter).toISOString() : undefined,
      };
      // Contacts mode hits a different endpoint (admin API) vs campaign mode (messaging API)
      const resp = mode === "contacts"
        ? await api.admin.post("Contacts/crm-preview", filterPayload)
        : await api.messaging.post("ContactLists/crm-preview", filterPayload);
      if (!resp.ok) throw new Error("Preview failed");
      const data = await resp.json();
      setPreview(data);
      setStep(3);
    } catch {
      setError("Could not load preview. Check your connection and try again.");
    } finally {
      setPreviewing(false);
    }
  }, [api, mode, source, filters]);

  // ── Step 3: import ──

  const runImport = useCallback(async () => {
    if (!source || !preview) return;
    if (mode === "campaign-list" && listMode === "new" && !newListName.trim()) {
      setError("Enter a name for the new list.");
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const filterPayload = {
        source:       source.id,
        requireEmail: filters.requireEmail,
        requirePhone: filters.requirePhone,
        company:      filters.company   || undefined,
        country:      filters.country   || undefined,
        createdAfter: filters.createdAfter ? new Date(filters.createdAfter).toISOString() : undefined,
      };

      if (mode === "contacts") {
        const resp = await api.admin.post("Contacts/import-from-crm", { filters: filterPayload });
        if (!resp.ok) throw new Error("Import failed");
        const data = await resp.json();
        onImported(0, `${data.added} contacts imported`);
      } else {
        const resp = await api.messaging.post("ContactLists/import-from-crm", {
          filters: filterPayload,
          newListName: listMode === "new" ? newListName.trim() : undefined,
          listId:      listMode === "existing" ? existingListId : undefined,
        });
        if (!resp.ok) throw new Error("Import failed");
        const data = await resp.json();
        onImported(data.listId, data.listName);
      }
    } catch {
      setError("Import failed. Try again or contact support.");
    } finally {
      setImporting(false);
    }
  }, [api, mode, source, preview, filters, listMode, newListName, existingListId, onImported]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Import Campaign Audience</h2>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3].map(n => (
                <React.Fragment key={n}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
                    ${step >= n ? "bg-cornflower-blue-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {n}
                  </div>
                  {n < 3 && <div className={`h-0.5 w-8 ${step > n ? "bg-cornflower-blue-400" : "bg-slate-200"}`} />}
                </React.Fragment>
              ))}
              <span className="text-xs text-slate-400 ml-2">
                {step === 1 ? "Choose source" : step === 2 ? "Set filters" : "Preview & import"}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── Step 1: source selection ── */}
          {step === 1 && (
            <div>
              <p className="text-sm text-slate-500 mb-4">
                Choose where to pull contacts from. All sources deduplicate by email — no duplicates will be created.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {SOURCES.map(s => (
                  <button key={s.id} disabled={!s.available}
                    onClick={() => { setSource(s); setStep(2); }}
                    className={`text-left px-4 py-3 border-2 rounded-lg transition-all
                      ${!s.available ? "opacity-50 cursor-not-allowed border-slate-200 bg-slate-50"
                        : source?.id === s.id ? "border-cornflower-blue-400 bg-cornflower-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}
                    style={s.available ? { borderColor: source?.id === s.id ? s.color : undefined } : {}}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{s.logo.length === 1 && /[A-Z]/.test(s.logo)
                        ? <span className="inline-flex w-6 h-6 rounded text-xs font-bold text-white items-center justify-center"
                            style={{ backgroundColor: s.color }}>{s.logo}</span>
                        : s.logo}</span>
                      <span className="text-sm font-semibold text-slate-700">{s.label}</span>
                      {!s.available && (
                        <span className="ml-auto text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Connect first</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.description}</p>
                    <p className="text-xs text-slate-400 mt-1.5 italic">{s.syncMode}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                HubSpot, Dynamics, Pipedrive, and Salesforce require an active integration.
                Go to <strong>Integrations</strong> to connect them, then come back to import.
              </p>
            </div>
          )}

          {/* ── Step 2: filters (Velaro native) ── */}
          {step === 2 && source?.id === "velaro" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-cornflower-blue-50 rounded-lg border border-cornflower-blue-200">
                <span className="inline-flex w-7 h-7 rounded text-sm font-bold text-white items-center justify-center bg-cornflower-blue-500">V</span>
                <div>
                  <div className="text-sm font-semibold text-slate-700">Velaro CRM</div>
                  <div className="text-xs text-slate-500">Filter which contacts to include</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={filters.requireEmail}
                    onChange={e => setFilters(f => ({ ...f, requireEmail: e.target.checked }))}
                    className="rounded border-slate-300" />
                  <span className="text-sm text-slate-700">Has email address</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={filters.requirePhone}
                    onChange={e => setFilters(f => ({ ...f, requirePhone: e.target.checked }))}
                    className="rounded border-slate-300" />
                  <span className="text-sm text-slate-700">Has phone number</span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Company name contains</label>
                <input type="text" value={filters.company}
                  onChange={e => setFilters(f => ({ ...f, company: e.target.value }))}
                  placeholder="e.g. Acme (leave blank for all)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Country</label>
                <input type="text" value={filters.country}
                  onChange={e => setFilters(f => ({ ...f, country: e.target.value }))}
                  placeholder="e.g. US (leave blank for all)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Created after</label>
                <input type="date" value={filters.createdAfter}
                  onChange={e => setFilters(f => ({ ...f, createdAfter: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-slate-400 mt-0.5">Leave blank to include all contacts.</p>
              </div>
            </div>
          )}

          {/* ── Step 2: not-connected stub ── */}
          {step === 2 && source && source.id !== "velaro" && (
            <div className="text-center py-10">
              <div className="text-4xl mb-3">{source.logo}</div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{source.label} not connected</h3>
              <p className="text-sm text-slate-500 mb-4 max-w-sm mx-auto">
                Connect {source.label} under <strong>Integrations</strong> first.
                Once connected, return here to import your contacts.
              </p>
              <button onClick={() => setStep(1)}
                className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">
                ← Choose a different source
              </button>
            </div>
          )}

          {/* ── Step 3: preview + destination ── */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Preview card */}
              {preview && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">
                      {preview.count.toLocaleString()} contact{preview.count !== 1 ? "s" : ""} matched
                    </span>
                    {preview.stub && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Integration not connected</span>
                    )}
                  </div>
                  {preview.preview.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Company</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.map((p, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-4 py-2 text-slate-700">{p.name || "—"}</td>
                            <td className="px-4 py-2 text-slate-500">{p.email}</td>
                            <td className="px-4 py-2 text-slate-400">{p.company || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="px-4 py-3 text-sm text-slate-500">{preview.message ?? "No contacts matched your filters."}</p>
                  )}
                  {preview.count > 5 && (
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-400">
                      Showing 5 of {preview.count.toLocaleString()}. All will be imported.
                    </div>
                  )}
                </div>
              )}

              {/* Destination */}
              {mode === "contacts" ? (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <strong>Importing into your Contact Manager.</strong> These contacts will appear under
                  Contact Center → Contacts and will automatically match to future conversations
                  from the same email address.
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">Add to list</label>
                  <div className="flex gap-3 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={listMode === "new"} onChange={() => setListMode("new")} />
                      <span className="text-sm text-slate-700">Create new list</span>
                    </label>
                    {existingListId && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={listMode === "existing"} onChange={() => setListMode("existing")} />
                        <span className="text-sm text-slate-700">Add to "{existingListName}"</span>
                      </label>
                    )}
                  </div>
                  {listMode === "new" && (
                    <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)}
                      placeholder="e.g. Q2 Prospects — Velaro CRM"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  )}
                </div>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          )}

          {error && step !== 3 && <p className="text-sm text-red-600 mt-3">{error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <button onClick={() => step > 1 ? setStep(s => (s - 1) as 1 | 2 | 3) : onClose()}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
            {step === 1 ? "Cancel" : "← Back"}
          </button>

          <div className="flex gap-2">
            {step === 2 && source?.id === "velaro" && (
              <button onClick={runPreview} disabled={previewing}
                className="px-5 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50">
                {previewing ? "Loading…" : "Preview →"}
              </button>
            )}
            {step === 3 && (
              <button onClick={runImport} disabled={importing || preview?.count === 0}
                className="px-5 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50">
                {importing ? "Importing…" : `Import ${preview?.count?.toLocaleString() ?? ""} Contacts`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
